/**
 * PackPreviewModal — Pack 카드/링크 클릭 시 띄우는 팩 상세 모달.
 *
 * BlockPreviewModal 과 동일한 `--dual-pane` 도감 레이아웃 재사용:
 *   왼쪽: 팩 소속 블럭 kind 들을 노드로, pack.slice.shapes 를 엣지로 묶은 관계 그래프 (sticky)
 *   오른쪽: Blocks / Kinds / Domains / Relations / Shapes 섹션 (스크롤)
 *
 * 블럭 카드 / 그래프 노드 클릭 시 `/blocks?block=<id>` 로 이동해 BlockPreviewModal 이 열린다.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import type { OntologyBlock, OntologyPack } from "../../api/admin-client.ts"
import { KnowledgeGraph, type KnowledgeGraphHandle } from "../graph/KnowledgeGraph.tsx"
import type { GraphEdge, GraphNode } from "../graph/types.ts"

interface PackPreviewModalProps {
	readonly pack: OntologyPack
	readonly allBlocks: ReadonlyArray<OntologyBlock>
	readonly onClose: () => void
}

export function PackPreviewModal(props: PackPreviewModalProps) {
	const { pack, allBlocks, onClose } = props
	const blockById = useMemo(() => new Map(allBlocks.map((b) => [b.id, b])), [allBlocks])
	const kindToBlock = useMemo(() => {
		const map = new Map<string, OntologyBlock>()
		for (const b of allBlocks) {
			const kind = b.slice.kinds[0]
			if (kind) map.set(kind.name, b)
		}
		return map
	}, [allBlocks])

	/** 팩 관계 그래프 — 팩 소속 kind 를 노드로, pack.slice.shapes 를 엣지로 묶는다. */
	const packGraph = useMemo(() => buildPackGraph(pack, kindToBlock), [pack, kindToBlock])
	const graphRef = useRef<KnowledgeGraphHandle>(null)

	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [onClose])

	return (
		<dialog
			className="drawer-backdrop"
			open
			aria-label={`${pack.displayName} 팩 상세`}
			onMouseDown={(e) => e.currentTarget === e.target && onClose()}
		>
			<aside
				className="drawer block-preview-drawer block-preview-drawer--dual-pane"
				onMouseDown={(e) => e.stopPropagation()}
				aria-modal="true"
			>
				<div className="modal-head">
					<button type="button" className="drawer-close" onClick={onClose} aria-label="닫기">
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<title>닫기</title>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
					<div className="block-preview-header">
						<span className="block-preview-icon" aria-hidden>
							{pack.icon}
						</span>
						<div className="block-preview-title">
							<h2>{pack.displayName}</h2>
							<div className="block-preview-meta">
								<span className={`badge blocks-page-card-cat pack-group-${pack.group}`}>{pack.group}</span>
								<PackIdBadge label="pack id" value={pack.id} />
							</div>
						</div>
					</div>
					<p>{pack.description}</p>
				</div>

				<div className="modal-body">
					<div className="block-preview-graph-side">
						{(packGraph.nodes.length > 0 || packGraph.edges.length > 0) && (
							<section className="block-preview-section block-preview-graph-section">
								<div className="block-preview-graph-head">
									<h3>관계 그래프</h3>
									<button
										type="button"
										className="btn btn-ghost btn-sm"
										onClick={() => graphRef.current?.fitView()}
										title="그래프를 캔버스에 맞춰 재정렬"
									>
										⟲ Fit
									</button>
								</div>
								<p className="muted block-preview-graph-hint">
									팩 소속 블럭 {pack.slice.kinds.length}개 kind 사이 관계를 한 장면으로 묶어 시각화. 노드 클릭
									시 해당 블럭으로 이동.
								</p>
								<div className="block-preview-graph-canvas">
									<KnowledgeGraph
										ref={graphRef}
										nodes={packGraph.nodes}
										edges={packGraph.edges}
										onNodeSelect={(node: GraphNode | null) => {
											if (!node) return
											// node.id === block.id 로 넣어뒀으므로 그대로 라우팅
											const next = allBlocks.find((b) => b.id === node.id)
											if (!next) return
											window.location.assign(`/blocks?block=${encodeURIComponent(next.id)}`)
										}}
									/>
								</div>
							</section>
						)}
					</div>

					<div className="block-preview-details-side">
						<section className="block-preview-section">
							<h3>블럭 ({pack.blockIds.length})</h3>
							{pack.blockIds.length === 0 ? (
								<p className="muted">포함된 블럭이 없습니다.</p>
							) : (
								<ul className="pack-preview-blocks">
									{pack.blockIds.map((id) => {
										const b = blockById.get(id)
										return (
											<li key={id}>
												<Link
													to={`/blocks?block=${encodeURIComponent(id)}`}
													className="pack-preview-block-link"
													onClick={onClose}
												>
													<span className="pack-preview-block-icon" aria-hidden>
														{b?.icon ?? "◼"}
													</span>
													<div className="pack-preview-block-body">
														<strong>{b?.displayName ?? id}</strong>
														{b?.description && <span className="muted">{b.description}</span>}
														<PackIdBadge label="block id" value={id} />
													</div>
													<span className="pack-preview-block-chevron" aria-hidden>
														›
													</span>
												</Link>
											</li>
										)
									})}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>종류 ({pack.slice.kinds.length})</h3>
							{pack.slice.kinds.length === 0 ? (
								<p className="muted">선언된 kind 가 없습니다.</p>
							) : (
								<ul className="block-preview-kinds">
									{pack.slice.kinds.map((k) => (
										<li key={k.name}>
											<span className="block-preview-kind-icon" aria-hidden>
												{k.icon ?? "◼"}
											</span>
											<div className="block-preview-kind-body">
												<strong>{k.displayName}</strong>
												<PackIdBadge label="kind id" value={k.name} />
												{k.description && <span className="muted">{k.description}</span>}
											</div>
										</li>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>도메인 ({pack.slice.domains.length})</h3>
							{pack.slice.domains.length === 0 ? (
								<p className="muted">선언된 domain 이 없습니다.</p>
							) : (
								<div className="block-preview-chips">
									{pack.slice.domains.map((d) => (
										<span key={d.name} className="chip" title={d.description ?? d.displayName}>
											{d.displayName}
											<PackIdBadge label="domain id" value={d.name} />
										</span>
									))}
								</div>
							)}
						</section>

						<section className="block-preview-section">
							<h3>관계 ({pack.slice.relations.length})</h3>
							{pack.slice.relations.length === 0 ? (
								<p className="muted">선언된 relation 이 없습니다.</p>
							) : (
								<ul className="block-preview-relations">
									{pack.slice.relations.map((r) => (
										<li key={r.name}>
											<PackIdBadge label="relation" value={r.name} emphasis />
											<strong>{r.displayName}</strong>
											{r.description && <span className="muted">{r.description}</span>}
										</li>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>형태 ({pack.slice.shapes.length})</h3>
							{pack.slice.shapes.length === 0 ? (
								<p className="muted">선언된 shape 이 없습니다.</p>
							) : (
								<ul className="pack-preview-shapes">
									{pack.slice.shapes.map((s) => {
										const src = kindToBlock.get(s.sourceKind)
										const tgt = kindToBlock.get(s.targetKind)
										return (
											<li
												key={`${s.sourceKind}|${s.relation}|${s.targetKind}`}
												className="pack-preview-shape"
												title={s.description}
											>
												<div className="shape-flow">
													<ShapeNode block={src} kind={s.sourceKind} onClose={onClose} />
													<div className="shape-flow__rel">
														<span className="shape-flow__rel-label">{s.relation}</span>
														<span className="shape-flow__arrow" aria-hidden>
															→
														</span>
													</div>
													<ShapeNode block={tgt} kind={s.targetKind} onClose={onClose} />
												</div>
											</li>
										)
									})}
								</ul>
							)}
						</section>
					</div>
				</div>

				<div className="modal-foot">
					<span className="drawer-hint muted" aria-hidden>
						ESC 로 닫기
					</span>
					<div style={{ display: "flex", gap: 8 }}>
						<ShareLinkButton packId={pack.id} />
						<button type="button" className="btn btn-primary" onClick={onClose}>
							닫기
						</button>
					</div>
				</div>
			</aside>
		</dialog>
	)
}

function ShapeNode(props: {
	readonly block: OntologyBlock | undefined
	readonly kind: string
	readonly onClose: () => void
}) {
	const { block, kind, onClose } = props
	if (!block) {
		return <span className="shape-flow__node shape-flow__node--orphan">{kind}</span>
	}
	return (
		<Link
			to={`/blocks?block=${encodeURIComponent(block.id)}`}
			className={`shape-flow__node cat-${block.category}`}
			title={block.description}
			onClick={(e) => {
				e.stopPropagation()
				onClose()
			}}
		>
			<span className="shape-flow__icon" aria-hidden>
				{block.icon}
			</span>
			<span className="shape-flow__label">{block.displayName}</span>
		</Link>
	)
}

function PackIdBadge(props: { readonly label: string; readonly value: string; readonly emphasis?: boolean }) {
	return (
		<span className={`id-badge${props.emphasis ? " id-badge--emphasis" : ""}`}>
			<span className="id-badge__label">{props.label}</span>
			<code className="id-badge__value">{props.value}</code>
		</span>
	)
}

function ShareLinkButton({ packId }: { readonly packId: string }) {
	const [copied, setCopied] = useState(false)
	const onClick = () => {
		const url = new URL(window.location.href)
		url.pathname = "/packs"
		url.searchParams.set("pack", packId)
		url.hash = ""
		void navigator.clipboard.writeText(url.toString()).catch(() => undefined)
		setCopied(true)
		setTimeout(() => setCopied(false), 1600)
	}
	return (
		<button
			type="button"
			className="btn btn-ghost"
			onClick={onClick}
			title="이 팩 상세로 바로 열리는 링크 복사"
		>
			{copied ? "복사됨 ✓" : "링크 복사"}
		</button>
	)
}

/**
 * 팩 관계 그래프 빌더 — 팩 소속 블럭들의 kind 를 노드로, pack.slice.shapes 를 엣지로 묶는다.
 * node.id 는 블럭 id (라우팅 용이), 노드 이름/카테고리는 실제 블럭 메타에서 가져와 색상/라벨 일치.
 */
function buildPackGraph(
	pack: OntologyPack,
	kindToBlock: ReadonlyMap<string, OntologyBlock>
): { readonly nodes: ReadonlyArray<GraphNode>; readonly edges: ReadonlyArray<GraphEdge> } {
	const kindToNodeId = new Map<string, string>()
	const nodes: GraphNode[] = []
	for (const k of pack.slice.kinds) {
		const block = kindToBlock.get(k.name)
		const nodeId = block?.id ?? k.name
		kindToNodeId.set(k.name, nodeId)
		nodes.push({
			id: nodeId,
			domain: block?.category ?? pack.group,
			kind: k.name,
			name: block?.displayName ?? k.displayName,
			category: block?.category ?? pack.group
		})
	}
	const edges: GraphEdge[] = []
	for (const s of pack.slice.shapes) {
		const src = kindToNodeId.get(s.sourceKind)
		const tgt = kindToNodeId.get(s.targetKind)
		if (!src || !tgt) continue
		edges.push({
			id: `pack:${src}|${s.relation}|${tgt}`,
			source: src,
			target: tgt,
			relation: s.relation
		})
	}
	return { nodes, edges }
}
